import { doesFilterStateMatchPermalinkIdentity } from '@/features/fanfiction-grid/utils/does-filter-state-match-permalink-identity';
import type { PermalinkIdentityFilters } from '@/features/fanfiction-grid/utils/does-filter-state-match-permalink-identity';
import { buildSeoDisabledFallbackUrl } from '@/features/fanfiction-grid/utils/build-seo-disabled-fallback-url';
import type { FilterState } from '@/features/fanfiction-grid/store';
import { getBrowserPathname, isOnPermalinkRoute } from './get-browser-pathname.util';

type FilterSlice = Pick<
  FilterState,
  | 'selectedFandoms'
  | 'selectedTags'
  | 'selectedUniverses'
  | 'selectedStatuses'
  | 'selectedRatings'
  | 'selectedCharacters'
  | 'selectedPairingCharacters'
>;

type SeoDisabledNavigationParams = {
  basePath: '/fanfics' | '/originals';
  currentFilters: FilterSlice;
  restoreCatalogSeoPage: () => void;
  clearSeoPage: () => void;
  /** Path-encoded identity remembered for the current permalink. */
  expectedIdentity?: PermalinkIdentityFilters | null;
};

/**
 * SEO redirect disabled/404: restore catalog chrome when filters are empty,
 * otherwise clear SEO; leave permalink URL only when identity still matches.
 */
export const applySeoDisabledNavigation = ({
  basePath,
  currentFilters,
  restoreCatalogSeoPage,
  clearSeoPage,
  expectedIdentity = null,
}: SeoDisabledNavigationParams): void => {
  const isEmptyCatalogFilters =
    currentFilters.selectedFandoms.length === 0 &&
    currentFilters.selectedTags.length === 0 &&
    currentFilters.selectedCharacters.length === 0 &&
    currentFilters.selectedPairingCharacters.length === 0;

  if (isEmptyCatalogFilters && !isOnPermalinkRoute()) {
    restoreCatalogSeoPage();
  } else {
    clearSeoPage();
  }

  if (!isOnPermalinkRoute()) return;

  const browserPathname = getBrowserPathname();
  if (doesFilterStateMatchPermalinkIdentity(browserPathname, currentFilters, expectedIdentity)) {
    return;
  }

  const fallbackUrl = buildSeoDisabledFallbackUrl(
    basePath,
    browserPathname,
    currentFilters,
    expectedIdentity,
  );
  window.history.pushState({}, '', fallbackUrl);
};
